/**
 * Echo Hunt -- verbatim phrase-overlap detection (the "lame tell" check, 2026-10-21).
 *
 * ## The problem
 *
 * A real bullet goes journal -> index (self-contained rewrite) -> reshape -> weave -> repair, and until this
 * check existed, no stage was ever asked to CHANGE the wording -- each was asked to keep the event, and kept
 * the phrasing along with it. Runs of 5-10 of the user's own words reached the final text, which is long
 * enough to recognise one's own phrasing (or the exact sentence) without any familiarity with the EVENT.
 * That is a shallow tell, and it bypasses the skill the game trains.
 *
 * ## The rule
 *
 * A candidate LEAKS when it shares a run of `PHRASE_LEAK_N` (5) consecutive words with a reference text AND
 * that run contains at least `PHRASE_LEAK_MIN_CONTENT_WORDS` (2) content words. The content-word condition is
 * what keeps the rule satisfiable: "I was in the" or "at the end of the" are not identifying, and banning
 * them would make every retelling fail.
 *
 * "Content word" is defined NEGATIVELY, as anything not in `FUNCTION_WORDS`. That is the clean way to do it:
 * English function words (articles, pronouns, prepositions, conjunctions, auxiliaries, a few common adverbs)
 * are a CLOSED class -- a fixed list of a couple of hundred words that does not grow -- whereas content words
 * are open-ended. So no part-of-speech tagger, no frequency table and no network: one Set lookup per token.
 * The cost of the approach is only that the list is English-specific, which the corpus is.
 *
 * ## Normalisation
 *
 * Lowercase; curly apostrophes unified; apostrophes then REMOVED inside a word ("didn't" -> "didnt", "Sam's"
 * -> "sams"), so a contraction cannot hide a match; everything else that is not a letter or digit separates
 * words. No stemming: "walked into the" vs "walking into the" is a weaker tell than an identical run, and a
 * stemmer would start flagging legitimate retellings.
 *
 * N-grams never cross from one reference text into the next -- the references are separate bullets, and a
 * run spanning two of them is not something the user ever wrote.
 *
 * PURE module: no React, no `store`, no DB imports.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §6 (P7 follow-up, 2026-10-21).
 */

/** Window length, in words. A run this long of the user's own wording is recognisable as theirs. */
export const PHRASE_LEAK_N = 5;

/** A window only counts when at least this many of its words are content words (see the header). */
export const PHRASE_LEAK_MIN_CONTENT_WORDS = 2;

/** English closed-class words, in their NORMALISED form (lowercase, apostrophes removed). */
export const FUNCTION_WORDS: ReadonlySet<string> = new Set([
	// articles + determiners
	"a", "an", "the", "this", "that", "these", "those", "some", "any", "no", "every", "each", "all", "both",
	"either", "neither", "another", "other", "such", "what", "which", "whatever", "whichever", "much", "many",
	"more", "most", "few", "fewer", "less", "least", "several", "own", "same",
	// pronouns (subject/object/possessive/reflexive) + contractions
	"i", "me", "my", "mine", "myself", "we", "us", "our", "ours", "ourselves", "you", "your", "yours", "yourself",
	"yourselves", "he", "him", "his", "himself", "she", "her", "hers", "herself", "it", "its", "itself", "they",
	"them", "their", "theirs", "themselves", "one", "ones", "someone", "somebody", "something", "anyone",
	"anybody", "anything", "everyone", "everybody", "everything", "nobody", "nothing", "who", "whom", "whose",
	"im", "ive", "id", "ill", "were", "weve", "wed", "youre", "youve", "youd", "youll", "hes", "shes", "theyre",
	"theyve", "theyd", "theyll", "thats", "theres", "heres", "whats", "whos", "lets",
	// prepositions
	"about", "above", "across", "after", "against", "along", "among", "around", "as", "at", "before", "behind",
	"below", "beneath", "beside", "besides", "between", "beyond", "by", "down", "during", "except", "for",
	"from", "in", "inside", "into", "like", "near", "of", "off", "on", "onto", "out", "outside", "over", "past",
	"since", "through", "throughout", "till", "to", "toward", "towards", "under", "underneath", "until", "up",
	"upon", "with", "within", "without", "via",
	// conjunctions + subordinators
	"and", "but", "or", "nor", "so", "yet", "because", "although", "though", "while", "whereas", "if", "unless",
	"whether", "than", "when", "whenever", "where", "wherever", "how", "why", "once",
	// auxiliaries + modals (incl. negated contractions)
	"be", "am", "is", "are", "was", "were", "been", "being", "have", "has", "had", "having", "do", "does", "did",
	"doing", "done", "will", "would", "shall", "should", "can", "could", "may", "might", "must", "ought",
	"isnt", "arent", "wasnt", "werent", "havent", "hasnt", "hadnt", "dont", "doesnt", "didnt", "wont",
	"wouldnt", "shouldnt", "cant", "couldnt", "mightnt", "mustnt",
	// common adverbs/particles that carry no identifying content
	"not", "there", "here", "then", "now", "just", "also", "too", "very", "really", "quite", "still", "even",
	"again", "ever", "never", "always", "only", "already", "almost", "back", "away", "together", "maybe",
	"perhaps", "rather", "somewhat", "instead", "else", "ago", "yes", "oh", "well", "anyway",
]);

/** Splits text into normalised word tokens (see the header's "Normalisation"). */
export function TokenizeForOverlap(text: string): string[] {
	const lower = text.toLowerCase().replace(/[\u2018\u2019\u02bc`]/g, "'");
	const matches = lower.match(/[a-z0-9]+(?:'[a-z0-9]+)*/g) ?? [];
	return matches.map(a=>a.replace(/'/g, ""));
}

export function IsContentWord(token: string): boolean {
	return !FUNCTION_WORDS.has(token);
}

export type FindPhraseLeaksOptions = {
	/** window length; default `PHRASE_LEAK_N` */
	n?: number;
	/** default `PHRASE_LEAK_MIN_CONTENT_WORDS` */
	minContentWords?: number;
};

/** Returns the runs of `candidate` that appear verbatim (after normalisation) in any of `references`.
 *
 * Overlapping/adjacent qualifying windows are MERGED into one maximal span, so a 9-word copy is reported once
 * as a 9-word phrase rather than as five overlapping 5-grams -- which is what a retry prompt wants to quote.
 * Spans are returned in normalised form (lowercase, no punctuation), de-duplicated, in candidate order.
 * An empty result means "no leak". */
export function FindPhraseLeaks(candidate: string, references: (string|n)[], options?: FindPhraseLeaksOptions): string[] {
	const n = Math.max(1, Math.round(options?.n ?? PHRASE_LEAK_N));
	const minContent = Math.max(0, options?.minContentWords ?? PHRASE_LEAK_MIN_CONTENT_WORDS);

	const referenceGrams = new Set<string>();
	for (const reference of references) {
		if (reference == null) continue;
		const tokens = TokenizeForOverlap(reference);
		for (let i = 0; i + n <= tokens.length; i++) {
			referenceGrams.add(tokens.slice(i, i + n).join(" "));
		}
	}
	if (referenceGrams.size == 0) return [];

	const tokens = TokenizeForOverlap(candidate);
	const flagged = new Array<boolean>(tokens.length).fill(false);
	for (let i = 0; i + n <= tokens.length; i++) {
		const window = tokens.slice(i, i + n);
		if (!referenceGrams.has(window.join(" "))) continue;
		if (window.filter(IsContentWord).length < minContent) continue;
		for (let j = i; j < i + n; j++) flagged[j] = true;
	}

	const spans: string[] = [];
	let start = -1;
	for (let i = 0; i <= tokens.length; i++) {
		if (i < tokens.length && flagged[i]) {
			if (start == -1) start = i;
		} else if (start != -1) {
			spans.push(tokens.slice(start, i).join(" "));
			start = -1;
		}
	}
	return [...new Set(spans)];
}
